<template>
  <main class="landing" id="main-content">
    <div class="landing__bg" aria-hidden="true" />

    <header class="landing__nav">
      <router-link to="/" class="landing__logo" aria-label="Workmorr home">
        <img class="landing__logo-img" :src="brandLogo" alt="Workmorr" width="180" height="34" />
      </router-link>
      <div class="landing__nav-cta">
        <StoreDownloadLink variant="play" size="compact" :href="playStoreUrl" />
        <StoreDownloadLink variant="apple" size="compact" :href="appStoreUrl" />
      </div>
    </header>

    <!-- Hero -->
    <section class="landing__hero" aria-labelledby="hero-heading">
      <div class="landing__hero-grid">
        <div class="landing__hero-copy landing__reveal">
          <p class="landing__pill">
            <span class="landing__pill-dot" aria-hidden="true" />
            Web retired — the app is where Workmorr lives now
          </p>
          <h1 id="hero-heading" class="landing__hero-title">
            Hire fast.
            <span class="landing__hl">Get hired.</span>
            <br />
            All in <span class="landing__hl landing__hl--soft">one place.</span>
          </h1>
          <p class="landing__hero-lead">
            Browse on a map, DM with confidence, and track every order—from first message to
            <span class="landing__em">done</span>.
          </p>
          <div class="landing__hero-actions">
            <StoreDownloadLink variant="play" size="prominent" :href="playStoreUrl" />
            <StoreDownloadLink variant="apple" size="prominent" :href="appStoreUrl" />
          </div>
        </div>

        <div class="landing__hero-visual landing__reveal landing__reveal--delay">
          <div class="landing__hero-orbit" aria-hidden="true" />
          <div class="landing__phone landing__phone--hero">
            <div class="landing__phone-frame">
              <img
                src="/media/landing/map-find-service.png"
                alt="Workmorr app map showing local services and providers nearby"
                width="390"
                height="844"
                loading="eager"
              />
            </div>
          </div>
          <div class="landing__phone landing__phone--float" aria-hidden="true">
            <div class="landing__phone-frame landing__phone-frame--sm">
              <img src="/media/landing/messages.png" alt="" width="320" height="693" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats -->
    <section class="landing__stats" aria-label="Highlights">
      <div class="landing__stats-inner">
        <div class="landing__stat-card landing__reveal">
          <p class="landing__stat-kicker">Discover</p>
          <p class="landing__stat-title">Map + list</p>
          <p class="landing__stat-desc">See what’s around you, then drill into the details.</p>
        </div>
        <div class="landing__stat-card landing__reveal landing__reveal--delay-sm">
          <p class="landing__stat-kicker">Trust</p>
          <p class="landing__stat-title">Verified pros</p>
          <p class="landing__stat-desc">Spot credibility before you ever send a message.</p>
        </div>
        <div class="landing__stat-card landing__reveal landing__reveal--delay-md">
          <p class="landing__stat-kicker">Clarity</p>
          <p class="landing__stat-title">Orders tracked</p>
          <p class="landing__stat-desc">Created, started, due, completed—always visible.</p>
        </div>
      </div>
    </section>

    <!-- Feature blocks -->
    <section class="landing__blocks" aria-labelledby="features-heading">
      <article class="landing__block landing__block--split">
        <div class="landing__block-text landing__reveal">
          <p class="landing__eyebrow">01 — Discover</p>
          <h3 class="landing__block-headline">
            Find services
            <span class="landing__hl">near you</span>
          </h3>
          <p class="landing__block-body">
            A live map and a scrollable directory—book home services, wellness, creative work, and
            more with real location context.
          </p>
          <ul class="landing__chips" aria-label="Feature tags">
            <li>Nearby pins</li>
            <li>Categories</li>
            <li>Provider names</li>
          </ul>
        </div>
        <div class="landing__block-media landing__reveal landing__reveal--delay-sm">
          <div class="landing__phone landing__phone--tilt-right">
            <div class="landing__phone-frame">
              <img
                src="/media/landing/map-find-service.png"
                alt="Workmorr map and Find a Service list"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </article>

      <article class="landing__block landing__block--split landing__block--reverse">
        <div class="landing__block-text landing__reveal">
          <p class="landing__eyebrow">02 — Trust</p>
          <h3 class="landing__block-headline">
            Chat with
            <span class="landing__hl">verified</span>
            talent
          </h3>
          <p class="landing__block-body">
            Inbox previews, usernames, and verification at a glance—so you move faster with
            confidence.
          </p>
          <ul class="landing__chips">
            <li>Read receipts</li>
            <li>Search</li>
            <li>Blue check</li>
          </ul>
        </div>
        <div class="landing__block-media landing__reveal landing__reveal--delay-sm">
          <div class="landing__phone landing__phone--tilt-left">
            <div class="landing__phone-frame">
              <img src="/media/landing/messages.png" alt="Messages inbox with verified users" loading="lazy" />
            </div>
          </div>
        </div>
      </article>

      <article class="landing__block landing__block--split landing__block--services">
        <div class="landing__block-text landing__reveal">
          <p class="landing__eyebrow">03 — Services &amp; orders</p>
          <h3 class="landing__block-headline">
            Listings that <span class="landing__hl">sell the work</span>,
            <span class="landing__hl landing__hl--soft">orders</span> that stay clear
          </h3>
          <p class="landing__block-body">
            Rich detail on your offer, transparent pricing and timelines, then one order screen for
            dates, status, and people—so nothing drifts out of sync.
          </p>
          <ul class="landing__chips" aria-label="Services and orders">
            <li>Media &amp; copy</li>
            <li>Price &amp; schedule</li>
            <li>Live status</li>
          </ul>
        </div>
        <div class="landing__block-media landing__reveal landing__reveal--delay-sm">
          <div class="landing__services-flow" role="group" aria-label="From listing to order">
            <div class="landing__services-flow-step">
              <span class="landing__services-flow-kicker">Service page</span>
              <div class="landing__phone landing__phone--flow">
                <div class="landing__phone-frame">
                  <img
                    src="/media/landing/view-service.png"
                    alt="Service listing with pricing and gallery"
                    loading="lazy"
                  />
                </div>
              </div>
              <p class="landing__services-flow-note">Show the work—not just the title.</p>
            </div>
            <div class="landing__services-flow-connector" aria-hidden="true">
              <span class="landing__services-flow-arrow" />
            </div>
            <div class="landing__services-flow-step">
              <span class="landing__services-flow-kicker">Order</span>
              <div class="landing__phone landing__phone--flow">
                <div class="landing__phone-frame">
                  <img
                    src="/media/landing/order-details.png"
                    alt="Order details with status and dates"
                    loading="lazy"
                  />
                </div>
              </div>
              <p class="landing__services-flow-note">Price, seller, dates, status—in one place.</p>
            </div>
          </div>
        </div>
      </article>

      <article class="landing__block landing__block--bento landing__block--bento-reverse">
        <div class="landing__bento-copy landing__reveal">
          <p class="landing__eyebrow">04 — Your space</p>
          <h3 class="landing__block-headline">
            Themes, alerts, and
            <span class="landing__hl">subscriptions</span>
          </h3>
          <p class="landing__block-body">
            Favorites, messages, account tools, light or dark UI, and push notifications for orders
            and chats—nothing important slips through.
          </p>
        </div>
        <div class="landing__bento-grid landing__reveal landing__reveal--delay-sm">
          <div class="landing__bento-card landing__bento-card--tall">
            <div class="landing__phone landing__phone--bento">
              <div class="landing__phone-frame">
                <img src="/media/landing/menu-drawer.png" alt="App navigation menu" loading="lazy" />
              </div>
            </div>
          </div>
          <div class="landing__bento-card">
            <div class="landing__phone landing__phone--bento landing__phone--short">
              <div class="landing__phone-frame">
                <img src="/media/landing/settings.png" alt="Settings with appearance and notifications" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </article>
    </section>

    <section class="landing__bottom" aria-labelledby="download-heading">
      <div class="landing__bottom-inner landing__reveal">
        <div class="landing__cta-strip">
          <h2 id="download-heading" class="landing__cta-strip-title">
            Get the app
            <span class="landing__cta-strip-accent">Google Play &amp; App Store</span>
          </h2>
          <div class="landing__cta-badges">
            <StoreDownloadLink variant="play" size="prominent" :href="playStoreUrl" />
            <StoreDownloadLink variant="apple" size="prominent" :href="appStoreUrl" />
          </div>
        </div>
      </div>
    </section>

    <footer class="landing__footer">
      <div class="landing__footer-links">
        <router-link to="/read/1/support/privacy-policy">Privacy Policy</router-link>
        <span class="landing__footer-dot" aria-hidden="true">·</span>
        <router-link to="/read/2/support/terms-of-use">Terms of Use</router-link>
      </div>
      <p class="landing__footer-note">© {{ year }} Workmorr. All rights reserved.</p>
    </footer>
  </main>
</template>

<script>
import brandLogo from '@/assets/logo.svg';
import StoreDownloadLink from '@/components/landing/StoreDownloadLink.vue';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.workmorr.workmorr';
const APP_STORE_URL = 'https://apps.apple.com/gh/app/workmorr/id6473766809';

export default {
  name: 'LandingView',
  components: {
    StoreDownloadLink,
  },
  data() {
    return {
      year: new Date().getFullYear(),
      brandLogo,
      playStoreUrl: PLAY_STORE_URL,
      appStoreUrl: APP_STORE_URL,
    };
  },
};
</script>

<style scoped>
/* Brand green matches src/assets/styles/main.css --workmorr-green (#008d36) */
.landing {
  --bg: #06080a;
  --bg-elevated: #0c0f14;
  --surface: #12161d;
  --surface-2: #181d26;
  --border: rgba(255, 255, 255, 0.08);
  --border-strong: rgba(255, 255, 255, 0.12);
  --text: #f5f7fa;
  --muted: #94a3b8;
  --accent: #008d36;
  --accent-bright: #00b344;
  --accent-deep: #006b29;
  --glow: rgba(0, 141, 54, 0.45);

  position: relative;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

.landing__bg {
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(ellipse 80% 55% at 50% -20%, rgba(0, 141, 54, 0.12), transparent 55%),
    radial-gradient(ellipse 60% 40% at 100% 30%, rgba(0, 141, 54, 0.04), transparent 50%),
    radial-gradient(ellipse 50% 35% at 0% 70%, rgba(0, 141, 54, 0.06), transparent 45%),
    var(--bg);
}

.landing > *:not(.landing__bg) {
  position: relative;
  z-index: 1;
}

.landing__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* Nav */
.landing__nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem 0;
}

.landing__logo {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  line-height: 0;
}

.landing__logo-img {
  height: 34px;
  width: auto;
  max-width: min(200px, 52vw);
  display: block;
}

.landing__nav-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
}

/* Hero */
.landing__hero {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 3.5rem;
}

.landing__hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: center;
}

@media (min-width: 960px) {
  .landing__hero-grid {
    grid-template-columns: 1fr 1.05fr;
    gap: 3rem;
    align-items: center;
  }
}

.landing__pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.25rem;
  padding: 0.45rem 1rem 0.45rem 0.65rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--muted);
  background: rgba(0, 141, 54, 0.08);
  border: 1px solid rgba(0, 141, 54, 0.22);
  border-radius: 999px;
}

.landing__pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 12px var(--glow);
  flex-shrink: 0;
}

.landing__hero-title {
  margin: 0 0 1.25rem;
  font-size: clamp(2.25rem, 6vw, 3.35rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.035em;
}

.landing__hl {
  color: var(--accent-bright);
  text-shadow: 0 0 36px rgba(0, 141, 54, 0.35);
}

.landing__hl--soft {
  color: var(--accent);
  text-shadow: none;
}

.landing__hero-lead {
  margin: 0 0 1.75rem;
  max-width: 28rem;
  font-size: 1.125rem;
  line-height: 1.65;
  color: var(--muted);
}

.landing__em {
  color: var(--text);
  font-weight: 600;
  padding: 0 0.15em;
  background: linear-gradient(180deg, transparent 60%, rgba(0, 141, 54, 0.28) 60%);
  border-radius: 2px;
}

.landing__hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem 1rem;
}

/* Hero visual — phone mockups */
.landing__hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  min-height: 420px;
  padding: 1rem 0 2rem;
}

@media (min-width: 960px) {
  .landing__hero-visual {
    min-height: 520px;
    justify-content: flex-end;
    padding-right: 1rem;
  }
}

.landing__hero-orbit {
  position: absolute;
  width: min(100%, 420px);
  height: min(100%, 420px);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 141, 54, 0.12) 0%, transparent 65%);
  filter: blur(2px);
  top: 50%;
  left: 50%;
  transform: translate(-45%, -55%);
}

.landing__phone {
  position: relative;
  z-index: 1;
}

.landing__phone--hero {
  width: min(100%, 300px);
  z-index: 2;
}

.landing__phone--float {
  position: absolute;
  width: min(52%, 200px);
  right: 4%;
  bottom: 8%;
  z-index: 3;
  animation: float-soft 5s ease-in-out infinite;
}

@media (min-width: 960px) {
  .landing__phone--float {
    right: 0;
    bottom: 12%;
    width: 200px;
  }
}

.landing__phone-frame {
  position: relative;
  padding: 10px;
  border-radius: 2.35rem;
  background: linear-gradient(160deg, #2b3240 0%, #151a22 50%, #0d1016 100%);
  box-shadow:
    0 32px 64px -16px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset,
    0 0 0 1px rgba(0, 0, 0, 0.5);
  transition: transform 0.35s ease;
}

.landing__phone-frame:hover {
  transform: translateY(-4px) scale(1.01);
}

.landing__phone-frame--sm {
  padding: 8px;
  border-radius: 1.85rem;
}

.landing__phone-frame img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 1.85rem;
  vertical-align: middle;
}

.landing__phone-frame--sm img {
  border-radius: 1.45rem;
}

@keyframes float-soft {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__phone--float {
    animation: none;
  }

  .landing__phone-frame:hover {
    transform: none;
  }
}

/* Reveal animation */
.landing__reveal {
  animation: reveal-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.landing__reveal--delay {
  animation-delay: 0.12s;
}

.landing__reveal--delay-sm {
  animation-delay: 0.08s;
}

.landing__reveal--delay-md {
  animation-delay: 0.16s;
}

@keyframes reveal-up {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__reveal,
  .landing__reveal--delay,
  .landing__reveal--delay-sm,
  .landing__reveal--delay-md {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Stats */
.landing__stats {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
}

.landing__stats-inner {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 720px) {
  .landing__stats-inner {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
}

.landing__stat-card {
  padding: 1.5rem 1.35rem;
  background: linear-gradient(145deg, var(--surface) 0%, var(--bg-elevated) 100%);
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.landing__stat-card:hover {
  border-color: rgba(0, 141, 54, 0.2);
  box-shadow: 0 20px 40px -24px rgba(0, 0, 0, 0.5);
}

.landing__stat-kicker {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}

.landing__stat-title {
  margin: 0 0 0.5rem;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.landing__stat-desc {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--muted);
}

.landing__features-title {
  margin: 0 auto 2.5rem;
  max-width: 36rem;
  font-size: clamp(1.35rem, 3vw, 1.65rem);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-align: center;
  color: var(--text);
}

/* Bottom: Get the app */
.landing__bottom {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 4rem;
}

/* ~7/12 of content width, centered (full width on small screens) */
.landing__bottom-inner {
  width: 100%;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .landing__bottom-inner {
    width: 58.333333%;
    max-width: 700px;
  }
}

.landing__cta-strip {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.35rem;
  padding: 1.75rem 1.25rem;
  border-radius: 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 16px 40px -24px rgba(0, 0, 0, 0.5);
}

@media (min-width: 640px) {
  .landing__cta-strip {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    gap: 1.25rem 2rem;
    padding: 1.65rem 2rem 1.65rem 2.25rem;
  }
}

.landing__cta-strip-title {
  margin: 0;
  font-size: clamp(1.35rem, 2.8vw, 1.75rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.landing__cta-strip-accent {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: 0;
}

/* Feature blocks */
.landing__blocks {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 5rem;
}

.landing__block {
  position: relative;
}

.landing__block--split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
}

@media (min-width: 900px) {
  .landing__block--split {
    grid-template-columns: 1fr 1fr;
    gap: 3.5rem;
  }

  .landing__block--reverse .landing__block-text {
    order: 2;
  }

  .landing__block--reverse .landing__block-media {
    order: 1;
  }
}

.landing__eyebrow {
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.landing__block-headline {
  margin: 0 0 1rem;
  font-size: clamp(1.75rem, 3.5vw, 2.35rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.03em;
}

.landing__block-body {
  margin: 0 0 1.25rem;
  font-size: 1.05rem;
  line-height: 1.65;
  color: var(--muted);
  max-width: 32rem;
}

.landing__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.landing__chips li {
  padding: 0.35rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border);
  border-radius: 999px;
}

.landing__block-media {
  display: flex;
  justify-content: center;
}

.landing__phone--tilt-right {
  width: min(100%, 320px);
}

.landing__phone--tilt-left {
  width: min(100%, 320px);
}

@media (min-width: 900px) {
  .landing__phone--tilt-right {
    transform: rotate(2deg);
  }

  .landing__phone--tilt-left {
    transform: rotate(-2deg);
  }

  .landing__phone--tilt-right:hover,
  .landing__phone--tilt-left:hover {
    transform: rotate(0deg) translateY(-6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__phone--tilt-right,
  .landing__phone--tilt-left {
    transform: none;
  }
}

/* Services → orders flow (section 03) */
.landing__block--services .landing__block-media {
  justify-content: center;
}

@media (min-width: 900px) {
  .landing__block--services .landing__block-media {
    justify-content: flex-end;
  }
}

.landing__services-flow {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
}

@media (min-width: 900px) {
  .landing__services-flow {
    max-width: none;
    margin: 0;
  }
}

.landing__services-flow-step {
  padding: 1.15rem 1rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  text-align: center;
}

.landing__services-flow-kicker {
  display: block;
  margin-bottom: 0.85rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
}

.landing__phone--flow {
  width: 100%;
  max-width: 220px;
  margin: 0 auto;
}

.landing__services-flow-note {
  margin: 0.85rem 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--muted);
}

.landing__services-flow-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 0;
  flex-shrink: 0;
}

.landing__services-flow-arrow {
  display: block;
  width: 2px;
  height: 1.75rem;
  border-radius: 2px;
  background: linear-gradient(180deg, var(--accent-deep), var(--accent-bright));
  box-shadow: 0 0 16px rgba(0, 141, 54, 0.35);
  position: relative;
}

.landing__services-flow-arrow::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -2px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid var(--accent-bright);
}

@media (min-width: 900px) {
  .landing__services-flow {
    flex-direction: row;
    align-items: stretch;
    gap: 0.75rem;
  }

  .landing__services-flow-step {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .landing__services-flow-connector {
    flex-direction: column;
    padding: 0 0.25rem;
    width: auto;
  }

  .landing__services-flow-arrow {
    width: 1.75rem;
    height: 2px;
    background: linear-gradient(90deg, var(--accent-deep), var(--accent-bright));
  }

  .landing__services-flow-arrow::after {
    left: auto;
    right: -2px;
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 7px solid var(--accent-bright);
    border-right: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__services-flow-arrow,
  .landing__services-flow-arrow::after {
    box-shadow: none;
  }
}

/* Bento */
.landing__block--bento {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
}

@media (min-width: 900px) {
  .landing__block--bento {
    grid-template-columns: 0.9fr 1.1fr;
    gap: 3rem;
  }
}

/* Phones first, copy second — reads as visuals left / text right on wide layouts */
.landing__block--bento.landing__block--bento-reverse .landing__bento-grid {
  order: 1;
}

.landing__block--bento.landing__block--bento-reverse .landing__bento-copy {
  order: 2;
}

.landing__bento-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 560px) {
  .landing__bento-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 1rem;
  }

  .landing__bento-card--tall {
    grid-row: span 2;
  }
}

.landing__bento-card {
  padding: 1rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.landing__bento-card--tall {
  min-height: 420px;
}

.landing__phone--bento {
  width: 100%;
  max-width: 260px;
}

.landing__phone--short {
  max-width: 220px;
}

.landing__cta-badges {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  justify-content: center;
  align-items: center;
}

@media (min-width: 640px) {
  .landing__cta-badges {
    justify-content: flex-end;
    flex: 1;
    min-width: min(100%, 400px);
  }
}

/* Footer */
.landing__footer {
  padding: 2rem 1.5rem 3rem;
  text-align: center;
  border-top: 1px solid var(--border);
}

.landing__footer-links {
  margin-bottom: 0.75rem;
}

.landing__footer-links a {
  color: var(--accent);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
}

.landing__footer-links a:hover {
  text-decoration: underline;
}

.landing__footer-dot {
  margin: 0 0.35rem;
  color: var(--muted);
}

.landing__footer-note {
  margin: 0;
  font-size: 0.8rem;
  color: var(--muted);
}
</style>
